import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './Branddetails.css';
import Loader from '../components/layout/preloader';

const BrandDetails = () => {
  const [aboutData, setAboutData] = useState([]);
  const [achievementPoints, setAchievementPoints] = useState([]);
  const [expectations, setExpectations] = useState([]);
  const [heroData, setHeroData] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const [vrInner, setVrInner] = useState([]);
  const [vrOuter, setVrOuter] = useState([]);
  const [eventDetails, setEventDetails] = useState([]);
  const [directoryStatic, setDirectoryStatic] = useState([]);

  const [modal, setModal] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false); // ← NEW

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const sectionsRef = useRef([]);
  const modalOverlayRef = useRef(null);
  const modalBoxRef = useRef(null);

  const fetchAll = async () => {
    const [ab, pts, ex, hr, se, vi, vo, ed, ds] = await Promise.all([
      supabase.from('details_about').select('*'),
      supabase.from('details_about_points').select('*').order('display_order'),
      supabase.from('details_expectations').select('*'),
      supabase.from('details_hero').select('*'),
      supabase.from('details_special_events').select('*').order('display_order'),
      supabase.from('details_vr_inner').select('*'),
      supabase.from('details_vr_outer').select('*'),
      supabase.from('event_details').select('*').order('display_order'),
      supabase.from('exhibitor_directory_static').select('*'),
    ]);
    setAboutData(ab.data || []);
    setAchievementPoints(pts.data || []);
    setExpectations(ex.data || []);
    setHeroData(hr.data || []);
    setSpecialEvents(se.data || []);
    setVrInner(vi.data || []);
    setVrOuter(vo.data || []);
    setEventDetails(ed.data || []);
    setDirectoryStatic(ds.data || []);
    setDataReady(true); // ← NEW: signal that rows are in the DOM
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Page-level animations (header + sections) ──────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 4 }
      );
      gsap.fromTo(sectionsRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.1, delay: 4.15 }
      );
    }, pageRef);
    return () => ctx.revert();
  }, []);

  // ── Table-row animations — fires once data is in the DOM ───────────────────
  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {
      const rows = pageRef.current?.querySelectorAll('[data-table-row]');
      if (!rows || rows.length === 0) return;

      // Set initial state — invisible & slightly left
      gsap.set(rows, { opacity: 0, x: -14 });

      // Stagger each row in after preloader (4s) + a small lead time (0.4s)
      gsap.to(rows, {
        opacity: 1,
        x: 0,
        duration: 0.38,
        ease: 'power2.out',
        stagger: 0.045,   // 45ms between each row — tight but readable
        delay: 4.4,       // starts just after sections begin revealing
      });
    }, pageRef);

    return () => ctx.revert();
  }, [dataReady]); // ← re-runs whenever data arrives

  // ── Modal animations ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!modal) return;
    gsap.fromTo(modalOverlayRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.25, ease: 'power2.out' }
    );
    gsap.fromTo(modalBoxRef.current,
      { opacity: 0, y: 32, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: 'power3.out' }
    );
  }, [modal]);

  const openEdit = (tableName, row, fieldDefs) => {
    const initial = {};
    fieldDefs.forEach(f => { initial[f.key] = row[f.key] || ''; });
    setFields(initial);
    setEditRow(row);
    setModal({ type: 'edit', table: tableName, fieldDefs });
  };

  const openAdd = (tableName, fieldDefs) => {
    const initial = {};
    fieldDefs.forEach(f => { initial[f.key] = ''; });
    setFields(initial);
    setEditRow(null);
    setModal({ type: 'add', table: tableName, fieldDefs });
  };

  const closeModal = () => {
    if (!modalOverlayRef.current || !modalBoxRef.current) {
      setModal(null); setEditRow(null); setFields({});
      return;
    }
    gsap.to(modalBoxRef.current, { opacity: 0, y: 20, scale: 0.97, duration: 0.22, ease: 'power2.in' });
    gsap.to(modalOverlayRef.current, {
      opacity: 0, duration: 0.22, ease: 'power2.in',
      onComplete: () => { setModal(null); setEditRow(null); setFields({}); }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    if (modal.type === 'edit') {
      await supabase.from(modal.table).update(fields).eq('id', editRow.id);
    } else {
      await supabase.from(modal.table).insert([fields]);
    }
    await fetchAll();
    closeModal();
    setLoading(false);
  };

  const handleDelete = async (tableName, id) => {
    const row = document.querySelector(`[data-row-id="${tableName}-${id}"]`);
    if (row) {
      await gsap.to(row, { opacity: 0, x: 16, duration: 0.22, ease: 'power2.in' });
    }
    await supabase.from(tableName).delete().eq('id', id);
    await fetchAll();
  };

  const setField = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  const Section = ({ title, onAdd, children, sectionIndex }) => (
    <div className="bd-section" ref={(el) => (sectionsRef.current[sectionIndex] = el)}>
      <div className="bd-section-header">
        <h2 className="bd-section-title">{title}</h2>
        {onAdd && (
          <button className="bd-btn-add" onClick={(e) => {
            gsap.fromTo(e.currentTarget, { scale: 0.93 }, { scale: 1, duration: 0.3, ease: 'elastic.out(1, 0.5)' });
            onAdd();
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add
          </button>
        )}
      </div>
      <div className="bd-table-wrapper">{children}</div>
    </div>
  );

  const ActionBtns = ({ onEdit, onDelete }) => (
    <div className="bd-actions">
      <button className="bd-action-btn bd-edit-btn" onClick={onEdit}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
      <button className="bd-action-btn bd-delete-btn" onClick={onDelete}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6M14 11v6"/>
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      </button>
    </div>
  );

  // ─────────────────────────── field definitions (unchanged) ─────────────────
  const aboutFields = [
    { key: 'title_en', label: 'Title (EN)' },
    { key: 'title_ar', label: 'Title (AR)', rtl: true },
    { key: 'content_en', label: 'Content (EN)', textarea: true },
    { key: 'content_ar', label: 'Content (AR)', textarea: true, rtl: true },
    { key: 'philosophy_title_en', label: 'Philosophy Title (EN)' },
    { key: 'philosophy_title_ar', label: 'Philosophy Title (AR)', rtl: true },
    { key: 'philosophy_content_en', label: 'Philosophy Content (EN)', textarea: true },
    { key: 'philosophy_content_ar', label: 'Philosophy Content (AR)', textarea: true, rtl: true },
  ];
  const pointFields = [
    { key: 'point_en', label: 'Point (EN)', textarea: true },
    { key: 'point_ar', label: 'Point (AR)', textarea: true, rtl: true },
    { key: 'display_order', label: 'Display Order' },
  ];
  const expectationFields = [
    { key: 'title_en', label: 'Title (EN)' },
    { key: 'title_ar', label: 'Title (AR)', rtl: true },
    { key: 'content_en', label: 'Content (EN)', textarea: true },
    { key: 'content_ar', label: 'Content (AR)', textarea: true, rtl: true },
    { key: 'booth_label_en', label: 'Booth Label (EN)' },
    { key: 'booth_label_ar', label: 'Booth Label (AR)', rtl: true },
    { key: 'booth_value', label: 'Booth Value' },
    { key: 'location_label_en', label: 'Location Label (EN)' },
    { key: 'location_label_ar', label: 'Location Label (AR)', rtl: true },
    { key: 'location_value_en', label: 'Location Value (EN)' },
    { key: 'location_value_ar', label: 'Location Value (AR)', rtl: true },
    { key: 'cta_text_en', label: 'CTA Text (EN)' },
    { key: 'cta_text_ar', label: 'CTA Text (AR)', rtl: true },
  ];
  const heroFields = [
    { key: 'back_btn_en', label: 'Back Button (EN)' },
    { key: 'back_btn_ar', label: 'Back Button (AR)', rtl: true },
    { key: 'label_en', label: 'Label (EN)' },
    { key: 'label_ar', label: 'Label (AR)', rtl: true },
    { key: 'title_en', label: 'Title (EN)' },
    { key: 'title_ar', label: 'Title (AR)', rtl: true },
    { key: 'subtitle_en', label: 'Subtitle (EN)', textarea: true },
    { key: 'subtitle_ar', label: 'Subtitle (AR)', textarea: true, rtl: true },
    { key: 'hero_image', label: 'Hero Image URL' },
  ];
  const specialEventFields = [
    { key: 'event_date_en', label: 'Date (EN)' },
    { key: 'event_date_ar', label: 'Date (AR)', rtl: true },
    { key: 'event_time', label: 'Time' },
    { key: 'event_title_en', label: 'Title (EN)' },
    { key: 'event_title_ar', label: 'Title (AR)', rtl: true },
    { key: 'display_order', label: 'Display Order' },
  ];
  const vrInnerFields = [
    { key: 'vr_url', label: 'VR URL' },
    { key: 'vr_title_en', label: 'VR Title (EN)' },
    { key: 'vr_title_ar', label: 'VR Title (AR)', rtl: true },
    { key: 'back_btn_en', label: 'Back Button (EN)' },
    { key: 'back_btn_ar', label: 'Back Button (AR)', rtl: true },
    { key: 'viewer_image', label: 'Viewer Image URL' },
  ];
  const vrOuterFields = [
    { key: 'title_en', label: 'Title (EN)' },
    { key: 'title_ar', label: 'Title (AR)', rtl: true },
    { key: 'subtitle_en', label: 'Subtitle (EN)', textarea: true },
    { key: 'subtitle_ar', label: 'Subtitle (AR)', textarea: true, rtl: true },
    { key: 'interactive_label_en', label: 'Interactive Label (EN)' },
    { key: 'interactive_label_ar', label: 'Interactive Label (AR)', rtl: true },
    { key: 'open_btn_en', label: 'Open Button (EN)' },
    { key: 'open_btn_ar', label: 'Open Button (AR)', rtl: true },
    { key: 'preview_image', label: 'Preview Image URL' },
  ];
  const eventDetailFields = [
    { key: 'title_en', label: 'Title (EN)' },
    { key: 'title_ar', label: 'Title (AR)', rtl: true },
    { key: 'content_en', label: 'Content (EN)', textarea: true },
    { key: 'content_ar', label: 'Content (AR)', textarea: true, rtl: true },
    { key: 'display_order', label: 'Display Order' },
  ];
  const dirStaticFields = [
    { key: 'page_tag', label: 'Page Tag' },
    { key: 'page_title', label: 'Page Title' },
    { key: 'page_description', label: 'Page Description', textarea: true },
    { key: 'stat_exhibitors_count', label: 'Exhibitors Count' },
    { key: 'stat_exhibitors_label', label: 'Exhibitors Label' },
    { key: 'stat_categories_count', label: 'Categories Count' },
    { key: 'stat_categories_label', label: 'Categories Label' },
    { key: 'stat_days_count', label: 'Days Count' },
    { key: 'stat_days_label', label: 'Days Label' },
    { key: 'search_placeholder', label: 'Search Placeholder' },
  ];

  const truncate = (str, n = 50) => str && str.length > n ? str.slice(0, n) + '…' : str;

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/brands">
        <div className="bd-page" ref={pageRef}>

          <div className="bd-page-header" ref={headerRef}>
            <div>
              <h1 className="bd-page-title">BRAND DEEP DETAIL</h1>
              <p className="bd-page-subtitle">Comprehensive exhibitor profile editor</p>
            </div>
          </div>

          {/* ── ABOUT & PHILOSOPHY ─────────────────────────────────────── */}
          <Section title="ABOUT & PHILOSOPHY" sectionIndex={0}>
            <table className="bd-table">
              <colgroup><col style={{width:'18%'}}/><col style={{width:'18%'}}/><col style={{width:'27%'}}/><col style={{width:'27%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>TITLE (EN)</th><th>TITLE (AR)</th><th>CONTENT (EN)</th><th>CONTENT (AR)</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {aboutData.map((row) => (
                  <tr key={row.id} data-row-id={`details_about-${row.id}`} data-table-row>
                    <td>{row.title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.title_ar}</td>
                    <td className="bd-trunc">{truncate(row.content_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.content_ar)}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_about', row, aboutFields)} onDelete={() => handleDelete('details_about', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── ACHIEVEMENT POINTS ─────────────────────────────────────── */}
          <Section title="ACHIEVEMENT POINTS" sectionIndex={1} onAdd={() => openAdd('details_about_points', pointFields)}>
            <table className="bd-table">
              <colgroup><col style={{width:'40%'}}/><col style={{width:'40%'}}/><col style={{width:'10%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>POINT (EN)</th><th>POINT (AR)</th><th>ORDER</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {achievementPoints.map((row) => (
                  <tr key={row.id} data-row-id={`details_about_points-${row.id}`} data-table-row>
                    <td className="bd-trunc">{truncate(row.point_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.point_ar)}</td>
                    <td>{row.display_order}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_about_points', row, pointFields)} onDelete={() => handleDelete('details_about_points', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── WHAT TO EXPECT ─────────────────────────────────────────── */}
          <Section title="WHAT TO EXPECT" sectionIndex={2}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'25%'}}/><col style={{width:'25%'}}/><col style={{width:'10%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>TITLE (EN)</th><th>TITLE (AR)</th><th>CONTENT (EN)</th><th>CONTENT (AR)</th><th>BOOTH</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {expectations.map((row) => (
                  <tr key={row.id} data-row-id={`details_expectations-${row.id}`} data-table-row>
                    <td>{row.title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.title_ar}</td>
                    <td className="bd-trunc">{truncate(row.content_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.content_ar)}</td>
                    <td>{row.booth_value}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_expectations', row, expectationFields)} onDelete={() => handleDelete('details_expectations', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── HERO SECTION ───────────────────────────────────────────── */}
          <Section title="HERO SECTION" sectionIndex={3}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'20%'}}/><col style={{width:'20%'}}/><col style={{width:'20%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>TITLE (EN)</th><th>TITLE (AR)</th><th>SUBTITLE (EN)</th><th>SUBTITLE (AR)</th><th>HERO IMAGE</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {heroData.map((row) => (
                  <tr key={row.id} data-row-id={`details_hero-${row.id}`} data-table-row>
                    <td>{row.title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.title_ar}</td>
                    <td className="bd-trunc">{truncate(row.subtitle_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.subtitle_ar)}</td>
                    <td>{row.hero_image ? <img src={row.hero_image} alt="hero" className="bd-thumb" /> : <span className="bd-empty">—</span>}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_hero', row, heroFields)} onDelete={() => handleDelete('details_hero', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── SPECIAL EVENTS ─────────────────────────────────────────── */}
          <Section title="SPECIAL EVENTS" sectionIndex={4} onAdd={() => openAdd('details_special_events', specialEventFields)}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'25%'}}/><col style={{width:'20%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>DATE (EN)</th><th>DATE (AR)</th><th>TIME</th><th>TITLE (EN)</th><th>TITLE (AR)</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {specialEvents.map((row) => (
                  <tr key={row.id} data-row-id={`details_special_events-${row.id}`} data-table-row>
                    <td>{row.event_date_en}</td>
                    <td dir="rtl" className="bd-ar">{row.event_date_ar}</td>
                    <td>{row.event_time}</td>
                    <td>{row.event_title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.event_title_ar}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_special_events', row, specialEventFields)} onDelete={() => handleDelete('details_special_events', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── VR INNER ───────────────────────────────────────────────── */}
          <Section title="VR — INNER VIEW" sectionIndex={5}>
            <table className="bd-table">
              <colgroup><col style={{width:'25%'}}/><col style={{width:'20%'}}/><col style={{width:'20%'}}/><col style={{width:'15%'}}/><col style={{width:'10%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>VR URL</th><th>TITLE (EN)</th><th>TITLE (AR)</th><th>VIEWER IMAGE</th><th>BACK BTN</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {vrInner.map((row) => (
                  <tr key={row.id} data-row-id={`details_vr_inner-${row.id}`} data-table-row>
                    <td className="bd-trunc">{truncate(row.vr_url, 30)}</td>
                    <td>{row.vr_title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.vr_title_ar}</td>
                    <td>{row.viewer_image ? <img src={row.viewer_image} alt="viewer" className="bd-thumb" /> : <span className="bd-empty">—</span>}</td>
                    <td>{row.back_btn_en}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_vr_inner', row, vrInnerFields)} onDelete={() => handleDelete('details_vr_inner', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── VR OUTER ───────────────────────────────────────────────── */}
          <Section title="VR — OUTER VIEW" sectionIndex={6}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'25%'}}/><col style={{width:'25%'}}/><col style={{width:'10%'}}/><col style={{width:'10%'}}/></colgroup>
              <thead><tr><th>TITLE (EN)</th><th>TITLE (AR)</th><th>SUBTITLE (EN)</th><th>SUBTITLE (AR)</th><th>PREVIEW</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {vrOuter.map((row) => (
                  <tr key={row.id} data-row-id={`details_vr_outer-${row.id}`} data-table-row>
                    <td>{row.title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.title_ar}</td>
                    <td className="bd-trunc">{truncate(row.subtitle_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.subtitle_ar)}</td>
                    <td>{row.preview_image ? <img src={row.preview_image} alt="preview" className="bd-thumb" /> : <span className="bd-empty">—</span>}</td>
                    <td><ActionBtns onEdit={() => openEdit('details_vr_outer', row, vrOuterFields)} onDelete={() => handleDelete('details_vr_outer', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── EVENT DETAILS ──────────────────────────────────────────── */}
          <Section title="EVENT DETAILS" sectionIndex={7} onAdd={() => openAdd('event_details', eventDetailFields)}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'28%'}}/><col style={{width:'28%'}}/><col style={{width:'6%'}}/><col style={{width:'8%'}}/></colgroup>
              <thead><tr><th>TITLE (EN)</th><th>TITLE (AR)</th><th>CONTENT (EN)</th><th>CONTENT (AR)</th><th>ORDER</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {eventDetails.map((row) => (
                  <tr key={row.id} data-row-id={`event_details-${row.id}`} data-table-row>
                    <td>{row.title_en}</td>
                    <td dir="rtl" className="bd-ar">{row.title_ar}</td>
                    <td className="bd-trunc">{truncate(row.content_en)}</td>
                    <td dir="rtl" className="bd-ar bd-trunc">{truncate(row.content_ar)}</td>
                    <td>{row.display_order}</td>
                    <td><ActionBtns onEdit={() => openEdit('event_details', row, eventDetailFields)} onDelete={() => handleDelete('event_details', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── EXHIBITOR DIRECTORY STATIC ─────────────────────────────── */}
          <Section title="EXHIBITOR DIRECTORY — STATIC" sectionIndex={8}>
            <table className="bd-table">
              <colgroup><col style={{width:'15%'}}/><col style={{width:'15%'}}/><col style={{width:'30%'}}/><col style={{width:'12%'}}/><col style={{width:'12%'}}/><col style={{width:'8%'}}/><col style={{width:'8%'}}/></colgroup>
              <thead><tr><th>PAGE TAG</th><th>PAGE TITLE</th><th>DESCRIPTION</th><th>EXHIBITORS</th><th>CATEGORIES</th><th>DAYS</th><th>ACTIONS</th></tr></thead>
              <tbody>
                {directoryStatic.map((row, i) => (
                  <tr key={i} data-row-id={`exhibitor_directory_static-${row.id}`} data-table-row>
                    <td>{row.page_tag}</td>
                    <td>{row.page_title}</td>
                    <td className="bd-trunc">{truncate(row.page_description)}</td>
                    <td>{row.stat_exhibitors_count} {row.stat_exhibitors_label}</td>
                    <td>{row.stat_categories_count} {row.stat_categories_label}</td>
                    <td>{row.stat_days_count} {row.stat_days_label}</td>
                    <td><ActionBtns onEdit={() => openEdit('exhibitor_directory_static', row, dirStaticFields)} onDelete={() => handleDelete('exhibitor_directory_static', row.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ── MODAL ─────────────────────────────────────────────────── */}
          {modal && (
            <div className="bd-modal-overlay" ref={modalOverlayRef} onClick={closeModal}>
              <div className="bd-modal" ref={modalBoxRef} onClick={(e) => e.stopPropagation()}>
                <div className="bd-modal-header">
                  <h2 className="bd-modal-title">{modal.type === 'add' ? 'ADD ROW' : 'EDIT ROW'}</h2>
                  <button className="bd-modal-close" onClick={closeModal}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="bd-modal-body">
                  {modal.fieldDefs.map((f) => (
                    <div className="bd-form-group" key={f.key}>
                      <label>{f.label}</label>
                      {f.textarea ? (
                        <textarea value={fields[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)} rows={3} dir={f.rtl ? 'rtl' : 'ltr'} placeholder={f.label} />
                      ) : (
                        <input type="text" value={fields[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)} dir={f.rtl ? 'rtl' : 'ltr'} placeholder={f.label} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="bd-modal-footer">
                  <button className="bd-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="bd-btn-confirm" onClick={handleSave} disabled={loading}>
                    {loading ? 'Saving...' : modal.type === 'add' ? 'Add' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </DashboardLayout>
    </>
  );
};

export default BrandDetails;